/**
 * Reward Center — Campaign Manager V2 endpoints.
 *
 * Shape of every endpoint here: unwrap the `{ data }` envelope in
 * `transformResponse`, hand the containers a normalized camelCase model, and
 * keep decimals as strings.
 *
 * Cache policy after a mutation follows §10 of the guide: `complete` and
 * `claim` answer from the write database and are authoritative, while the read
 * endpoints may be served by a replica. So the mutation response is patched
 * straight into the cached task instead of invalidating the task list, which
 * would refetch a possibly stale copy and undo the change in front of the user.
 *
 * The campaign, special campaign, task list and lottery summary reads take
 * `guest`: a guest gets the public route, whose response has no personal
 * fields (no progress, status, claim or tickets). History, complete and claim
 * are personal only.
 */
import { api } from '../api';
import {
  CAMPAIGNS,
  CAMPAIGN_LOTTERY,
  CAMPAIGN_LOTTERY_WINNERS,
  CAMPAIGN_TASKS,
  CAMPAIGN_TASK_CLAIM,
  CAMPAIGN_TASK_COMPLETE,
  LOTTERIES,
  PUBLIC_CAMPAIGNS,
  PUBLIC_CAMPAIGN_LOTTERY,
  PUBLIC_CAMPAIGN_TASKS,
  PUBLIC_SPECIAL_CAMPAIGN,
  REWARD_HISTORY,
  SPECIAL_CAMPAIGN,
  withPathParams
} from '@/constants/rewardCenter';
import {
  normalizeCampaign,
  normalizeHistoryPage,
  normalizeListEnvelope,
  normalizeLottery,
  normalizeLotterySummary,
  normalizeSpecialCampaign,
  normalizeTask,
  normalizeTaskMutationResult,
  normalizeWinner,
  unwrap
} from './normalize';
import {
  Campaign,
  CampaignListParams,
  CampaignViewDto,
  ClaimTaskParams,
  EnvelopeDto,
  GuestParam,
  HistoryItem,
  HistoryItemDto,
  HistoryParams,
  ListEnvelopeDto,
  Lottery,
  LotteryListItemDto,
  LotterySummary,
  LotterySummaryDto,
  LotterySummaryParams,
  LotteryWinner,
  LotteryWinnerDto,
  LotteryWinnersParams,
  PageParams,
  Paged,
  Task,
  TaskListParams,
  TaskMutationResult,
  TaskMutationResultDto,
  TaskRefParams,
  TaskViewDto
} from './types';

type QueryValue = string | number | boolean | undefined;

/** Drops empty values so we never send `?tag=&category=`. */
const buildQuery = (params: Record<string, QueryValue>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

const pageQuery = ({ page, pageLimit }: PageParams) => ({
  page,
  page_limit: pageLimit
});

/** The slice of an RTK Query cache entry this scan needs. */
interface CacheEntry {
  status?: string;
  endpointName?: string;
  originalArgs?: TaskListParams;
}

/**
 * Writes a task returned by a mutation into every cached task list that holds
 * it.
 *
 * RTK Query can only patch a cache entry by its exact arguments, and the task
 * list is cached per page and per filter combination — so the live cache is
 * scanned for entries that could contain this task rather than guessing the
 * arguments the UI happened to use. A list filtered by a status the task no
 * longer has (e.g. "todo" after a claim) drops it instead.
 */
const patchTaskInCaches = (
  task: Task,
  dispatch: (action: unknown) => void,
  getState: () => unknown
) => {
  if (!task.id) return;
  const state = getState() as Record<
    string,
    { queries?: Record<string, CacheEntry> }
  >;
  const queries = state?.[api.reducerPath]?.queries ?? {};

  Object.values(queries).forEach((entry) => {
    if (
      !entry ||
      entry.status !== 'fulfilled' ||
      !entry.originalArgs ||
      entry.endpointName !== 'getCampaignTasks'
    )
      return;
    const args = entry.originalArgs;
    const stillMatches =
      (!args.userStatus || args.userStatus === task.userStatus) &&
      (!args.activityStatus ||
        args.activityStatus === 'ALL' ||
        args.activityStatus === task.activityStatus);

    dispatch(
      rewardCenterSlice.util.updateQueryData(
        'getCampaignTasks',
        args,
        (draft) => {
          const index = draft.items.findIndex((item) => item.id === task.id);
          if (index === -1) return;
          // Immer draft: assigning into it is how RTK Query patches a cache.
          if (stillMatches) draft.items[index] = task;
          else draft.items.splice(index, 1);
        }
      )
    );
  });
};

/**
 * Patches the authoritative task from a claim/complete response into the
 * cache. A failed request is the caller's to report (it awaits `unwrap()`), so
 * the rejection is swallowed here rather than surfacing as an unhandled error.
 */
const patchOnSuccess = async (
  _args: unknown,
  {
    dispatch,
    getState,
    queryFulfilled
  }: {
    dispatch: (action: unknown) => void;
    getState: () => unknown;
    queryFulfilled: Promise<{ data: TaskMutationResult }>;
  }
) => {
  try {
    const { data } = await queryFulfilled;
    patchTaskInCaches(data.task, dispatch, getState);
  } catch {
    // Reported by the caller.
  }
};

export const rewardCenterSlice = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Campaign list with the caller's own progress. Server-ordered: special
     * first, then `sort_order`, then `starts_at` — do not re-sort client side.
     */
    getCampaigns: build.query<Paged<Campaign>, CampaignListParams | void>({
      query: (params) => {
        const { activityStatus, tag, guest, ...page } = params || {};
        return `${guest ? PUBLIC_CAMPAIGNS : CAMPAIGNS}${buildQuery({
          activity_status: activityStatus,
          tag,
          ...pageQuery(page)
        })}`;
      },
      transformResponse: (response: ListEnvelopeDto<CampaignViewDto>) =>
        normalizeListEnvelope(response, normalizeCampaign),
      providesTags: ['RewardCampaigns']
    }),

    /** `data.campaign: null` is the normal "none running" answer, not an error. */
    getSpecialCampaign: build.query<Campaign | null, GuestParam | void>({
      query: (params) => {
        const { guest } = params || {};
        return guest ? PUBLIC_SPECIAL_CAMPAIGN : SPECIAL_CAMPAIGN;
      },
      transformResponse: normalizeSpecialCampaign,
      providesTags: ['RewardCampaigns']
    }),

    getCampaignTasks: build.query<Paged<Task>, TaskListParams>({
      query: ({
        campaignId,
        activityStatus,
        userStatus,
        category,
        guest,
        ...page
      }) =>
        `${withPathParams(guest ? PUBLIC_CAMPAIGN_TASKS : CAMPAIGN_TASKS, {
          campaign_id: campaignId
        })}${buildQuery({
          activity_status: activityStatus,
          // The public list takes no user status.
          user_status: guest ? undefined : userStatus,
          category,
          ...pageQuery(page)
        })}`,
      transformResponse: (
        response: ListEnvelopeDto<TaskViewDto>,
        _meta,
        { campaignId }
      ) =>
        normalizeListEnvelope(response, (dto) =>
          normalizeTask(dto, campaignId)
        ),
      providesTags: (_result, _error, { campaignId }) => [
        { type: 'RewardTasks', id: campaignId }
      ]
    }),

    /**
     * Idempotent; a retry answers `already_applied: true`, which is a success.
     * Never call this unless `task.selfCompletable` — the back end rejects
     * every other evaluation type with TASK_NOT_SELF_COMPLETABLE.
     */
    completeTask: build.mutation<TaskMutationResult, TaskRefParams>({
      query: ({ campaignId, taskId }) => ({
        url: withPathParams(CAMPAIGN_TASK_COMPLETE, {
          campaign_id: campaignId,
          task_id: taskId
        }),
        method: 'POST',
        body: {}
      }),
      transformResponse: (
        response: EnvelopeDto<TaskMutationResultDto>,
        _meta,
        { campaignId }
      ) => normalizeTaskMutationResult(unwrap(response), campaignId),
      onQueryStarted: patchOnSuccess,
      // The task cache is patched above rather than invalidated; only the
      // derived views (history, draw, campaign progress) are refetched.
      invalidatesTags: ['RewardHistory', 'RewardLottery', 'RewardCampaigns']
    }),

    /**
     * Claims every reward on the task atomically. Omitting `cycle_key` lets the
     * server derive the current cycle, which is what the UI wants for anything
     * but an explicitly chosen past cycle.
     */
    claimTaskReward: build.mutation<TaskMutationResult, ClaimTaskParams>({
      query: ({ campaignId, taskId, cycleKey }) => ({
        url: withPathParams(CAMPAIGN_TASK_CLAIM, {
          campaign_id: campaignId,
          task_id: taskId
        }),
        method: 'POST',
        body: cycleKey ? { cycle_key: cycleKey } : {}
      }),
      transformResponse: (
        response: EnvelopeDto<TaskMutationResultDto>,
        _meta,
        { campaignId }
      ) => normalizeTaskMutationResult(unwrap(response), campaignId),
      onQueryStarted: patchOnSuccess,
      invalidatesTags: ['RewardHistory', 'RewardLottery', 'RewardCampaigns']
    }),

    /** One feed for challenges / rewards / completed campaigns. */
    getRewardHistory: build.query<Paged<HistoryItem>, HistoryParams | void>({
      query: (params) => {
        const { type, activityStatus, ...page } = params || {};
        return `${REWARD_HISTORY}${buildQuery({
          type,
          activity_status: activityStatus,
          ...pageQuery(page)
        })}`;
      },
      transformResponse: (response: ListEnvelopeDto<HistoryItemDto>) =>
        normalizeHistoryPage(unwrap(response)),
      providesTags: ['RewardHistory']
    }),

    /**
     * Ticket count, weight and countdown for one campaign cycle. A guest's
     * summary has no tickets or weight.
     */
    getCampaignLottery: build.query<LotterySummary, LotterySummaryParams>({
      query: ({ campaignId, cycleKey, guest }) =>
        `${withPathParams(guest ? PUBLIC_CAMPAIGN_LOTTERY : CAMPAIGN_LOTTERY, {
          campaign_id: campaignId
        })}${buildQuery({ cycle_key: cycleKey })}`,
      transformResponse: (response: EnvelopeDto<LotterySummaryDto>) =>
        normalizeLotterySummary(unwrap(response)),
      providesTags: ['RewardLottery']
    }),

    /** Completed draws, newest first. */
    getLotteries: build.query<Paged<Lottery>, PageParams | void>({
      query: (params) => `${LOTTERIES}${buildQuery(pageQuery(params || {}))}`,
      transformResponse: (response: ListEnvelopeDto<LotteryListItemDto>) =>
        normalizeListEnvelope(response, normalizeLottery),
      providesTags: ['RewardLottery']
    }),

    /**
     * Masked winners for a drawn cycle. Public, so the same for guests and
     * users. Callers treat a failure as "no winners to show" rather than an
     * error state — see WinnersCampaign.
     */
    getLotteryWinners: build.query<Paged<LotteryWinner>, LotteryWinnersParams>({
      query: ({ campaignId, cycleKey, ...page }) =>
        `${withPathParams(CAMPAIGN_LOTTERY_WINNERS, {
          campaign_id: campaignId
        })}${buildQuery({ cycle_key: cycleKey, ...pageQuery(page) })}`,
      transformResponse: (response: ListEnvelopeDto<LotteryWinnerDto>) =>
        normalizeListEnvelope(response, normalizeWinner),
      providesTags: ['RewardLottery']
    })
  }),
  overrideExisting: false
});

export const {
  useGetCampaignsQuery,
  useLazyGetCampaignsQuery,
  useGetSpecialCampaignQuery,
  useGetCampaignTasksQuery,
  useCompleteTaskMutation,
  useClaimTaskRewardMutation,
  useGetRewardHistoryQuery,
  useLazyGetRewardHistoryQuery,
  useGetCampaignLotteryQuery,
  useGetLotteriesQuery,
  useLazyGetLotteriesQuery,
  useGetLotteryWinnersQuery
} = rewardCenterSlice;
